import * as React from "react"
import { useState } from "react"
import { useSignal } from "@preact/signals-react"
import { Grid, FormControlLabel, Checkbox, Box, Typography, TextField, InputAdornment, IconButton, Popover, Tooltip, FormControl, InputLabel, Select, MenuItem } from "@mui/material"
import { AccordionSeeker, IconSeeker } from "@ui-seeker/core"

import { onlyNewProducts, onlyUnmapped, groupByField, searchText, newProductsDateFrom, newProductsDateTo, recentDataEnabled, recentDataDays, recentDataDateFrom, recentDataDateTo } from "../@signals"
import ProductsReviewMarketFilter from "./MarketFilter"
import ProductsReviewSitesFilter from "./SitesFilter"
import ProductsReviewBrandsFilter from "./BrandsFilter"
import ProductsReviewFiltersFooter from "./Footer"


// Helper to format date for display
function formatDateLabel(from, to) {
    if (!from && !to) return 'Only new (7d)'
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : null
    if (from && to) return `New: ${formatDate(from)} - ${formatDate(to)}`
    if (from) return `New: from ${formatDate(from)}`
    if (to) return `New: until ${formatDate(to)}`
    return 'Only new (7d)'
}

// Helper to format recent data label
function formatRecentDataLabel(days, from, to) {
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : null
    if (from || to) {
        if (from && to) return `Recent: ${formatDate(from)} - ${formatDate(to)}`
        if (from) return `Recent: from ${formatDate(from)}`
        if (to) return `Recent: until ${formatDate(to)}`
    }
    return `Recent data (${days}d)`
}

export default function ProductsReviewFilters() {
    const isOpen = useSignal(true)
    const [dateAnchor, setDateAnchor] = useState(null)
    const [recentDataAnchor, setRecentDataAnchor] = useState(null)

    const hasCustomDates = newProductsDateFrom.value || newProductsDateTo.value
    const hasCustomRecentDates = recentDataDateFrom.value || recentDataDateTo.value
    const hasCustomRecentDays = recentDataDays.value !== 30

    const handleClearDates = () => {
        newProductsDateFrom.value = null
        newProductsDateTo.value = null
    }

    const handleClearRecentData = () => {
        recentDataDays.value = 30
        recentDataDateFrom.value = null
        recentDataDateTo.value = null
    }

    return <AccordionSeeker
        title="Filters"
        TransitionProps={{ unmountOnExit: false }}
        expanded={isOpen.value}
        onChange={() => isOpen.value = !isOpen.value}
        footerActions={<ProductsReviewFiltersFooter />}
    >
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
                <ProductsReviewMarketFilter />
            </Grid>
            <Grid item xs={12} md={3}>
                <ProductsReviewSitesFilter />
            </Grid>
            <Grid item xs={12} md={3}>
                <ProductsReviewBrandsFilter />
            </Grid>
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Filter by text"
                    value={searchText.value}
                    onChange={(e) => searchText.value = e.target.value}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconSeeker icon="search" sx={{ fontSize: 16, color: '#9ca3af' }} />
                            </InputAdornment>
                        )
                    }}
                />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {/* Recent Data Filter */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={recentDataEnabled.value}
                                onChange={(e) => recentDataEnabled.value = e.target.checked}
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconSeeker icon="clock-rotate-left" sx={{ fontSize: 14, color: '#10b981' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatRecentDataLabel(recentDataDays.value, recentDataDateFrom.value, recentDataDateTo.value)}
                                </Typography>
                            </Box>
                        }
                        sx={{ m: 0 }}
                    />
                    <Tooltip title="Configure recent data range">
                        <IconButton
                            size="small"
                            onClick={(e) => setRecentDataAnchor(e.currentTarget)}
                            sx={{
                                ml: 0.5,
                                color: (hasCustomRecentDates || hasCustomRecentDays) ? '#10b981' : '#9ca3af',
                                '&:hover': { color: '#10b981' }
                            }}
                        >
                            <IconSeeker icon="sliders" sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    <Popover
                        open={Boolean(recentDataAnchor)}
                        anchorEl={recentDataAnchor}
                        onClose={() => setRecentDataAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
                            <Typography variant="subtitle2" sx={{ color: '#10b981', mb: 0.5 }}>
                                Recent data filter (by end_date)
                            </Typography>
                            <TextField
                                label="Days"
                                type="number"
                                size="small"
                                fullWidth
                                value={recentDataDays.value}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10)
                                    if (val > 0) recentDataDays.value = val
                                }}
                                InputProps={{ inputProps: { min: 1 } }}
                                disabled={hasCustomRecentDates}
                                helperText={hasCustomRecentDates ? "Using custom date range" : "Products with end_date in last N days"}
                            />
                            <Typography variant="caption" sx={{ color: '#9ca3af', textAlign: 'center' }}>
                                — or use custom dates —
                            </Typography>
                            <TextField
                                label="From"
                                type="date"
                                size="small"
                                fullWidth
                                value={recentDataDateFrom.value || ''}
                                onChange={(e) => recentDataDateFrom.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="To"
                                type="date"
                                size="small"
                                fullWidth
                                value={recentDataDateTo.value || ''}
                                onChange={(e) => recentDataDateTo.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            {(hasCustomRecentDates || hasCustomRecentDays) && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#10b981', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        onClick={handleClearRecentData}
                                    >
                                        Reset to default (30d)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Popover>
                </Box>

                {/* Only New Products Filter */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={onlyNewProducts.value}
                                onChange={(e) => onlyNewProducts.value = e.target.checked}
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconSeeker icon="sparkles" sx={{ fontSize: 14, color: '#6366f1' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {formatDateLabel(newProductsDateFrom.value, newProductsDateTo.value)}
                                </Typography>
                            </Box>
                        }
                        sx={{ m: 0 }}
                    />
                    <Tooltip title="Custom date range">
                        <IconButton
                            size="small"
                            onClick={(e) => setDateAnchor(e.currentTarget)}
                            sx={{
                                ml: 0.5,
                                color: hasCustomDates ? '#6366f1' : '#9ca3af',
                                '&:hover': { color: '#6366f1' }
                            }}
                        >
                            <IconSeeker icon="calendar-days" sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    <Popover
                        open={Boolean(dateAnchor)}
                        anchorEl={dateAnchor}
                        onClose={() => setDateAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
                            <Typography variant="subtitle2" sx={{ color: '#6366f1', mb: 0.5 }}>
                                Custom date range (first seen)
                            </Typography>
                            <TextField
                                label="From"
                                type="date"
                                size="small"
                                fullWidth
                                value={newProductsDateFrom.value || ''}
                                onChange={(e) => newProductsDateFrom.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="To"
                                type="date"
                                size="small"
                                fullWidth
                                value={newProductsDateTo.value || ''}
                                onChange={(e) => newProductsDateTo.value = e.target.value || null}
                                InputLabelProps={{ shrink: true }}
                            />
                            {hasCustomDates && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#6366f1', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        onClick={handleClearDates}
                                    >
                                        Reset to default (7d)
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Popover>
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={onlyUnmapped.value}
                            onChange={(e) => onlyUnmapped.value = e.target.checked}
                            size="small"
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconSeeker icon="link-slash" sx={{ fontSize: 14, color: '#ef4444' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Only unmapped
                            </Typography>
                        </Box>
                    }
                    sx={{ m: 0 }}
                />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Group By</InputLabel>
                    <Select
                        label="Group By"
                        value={groupByField.value || ''}
                        onChange={(e) => groupByField.value = e.target.value || null}
                    >
                        <MenuItem value="">No grouping</MenuItem>
                        <MenuItem value="reference">Reference</MenuItem>
                        <MenuItem value="product_url">Product URL</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    </AccordionSeeker>
}
